import type { AudioPlayer, PlaybackState } from "./types";

/**
 * Web implementation of AudioPlayer using HTMLAudioElement.
 */
export function createWebAudioPlayer(): AudioPlayer {
  const audio = new Audio();
  audio.crossOrigin = "anonymous";

  let _isPlaying = false;
  let _duration = 0;
  let _currentTime = 0;

  const stateListeners = new Set<(state: PlaybackState) => void>();
  const timeListeners = new Set<(time: number) => void>();
  const errorListeners = new Set<(error: Error) => void>();

  function notifyStateChange() {
    const state: PlaybackState = {
      isPlaying: _isPlaying,
      isLoaded: audio.readyState >= HTMLMediaElement.HAVE_METADATA,
      duration: _duration,
      position: _currentTime,
    };
    for (const listener of stateListeners) {
      listener(state);
    }
  }

  // Wire up audio element events
  audio.addEventListener("play", () => {
    _isPlaying = true;
    notifyStateChange();
  });

  audio.addEventListener("pause", () => {
    _isPlaying = false;
    notifyStateChange();
  });

  audio.addEventListener("ended", () => {
    _isPlaying = false;
    notifyStateChange();
  });

  audio.addEventListener("loadedmetadata", () => {
    _duration = audio.duration || 0;
    notifyStateChange();
  });

  audio.addEventListener("durationchange", () => {
    _duration = audio.duration || 0;
    notifyStateChange();
  });

  audio.addEventListener("timeupdate", () => {
    _currentTime = audio.currentTime;
    for (const listener of timeListeners) {
      listener(_currentTime);
    }
  });

  audio.addEventListener("error", () => {
    const error = new Error(audio.error?.message || "Audio playback error");
    for (const listener of errorListeners) {
      listener(error);
    }
  });

  return {
    async load(url: string): Promise<void> {
      return new Promise((resolve, reject) => {
        const handleLoaded = () => {
          cleanup();
          resolve();
        };
        const handleError = () => {
          cleanup();
          reject(new Error(audio.error?.message || "Failed to load audio"));
        };
        const cleanup = () => {
          audio.removeEventListener("loadedmetadata", handleLoaded);
          audio.removeEventListener("error", handleError);
        };

        audio.addEventListener("loadedmetadata", handleLoaded, { once: true });
        audio.addEventListener("error", handleError, { once: true });
        audio.src = url;
        audio.load();
      });
    },

    async play(): Promise<void> {
      await audio.play();
    },

    async pause(): Promise<void> {
      audio.pause();
    },

    async seekTo(seconds: number): Promise<void> {
      if (audio.readyState >= HTMLMediaElement.HAVE_METADATA) {
        audio.currentTime = seconds;
      } else {
        // Wait for metadata before seeking
        return new Promise((resolve) => {
          const handleMetadata = () => {
            audio.currentTime = seconds;
            resolve();
          };
          audio.addEventListener("loadedmetadata", handleMetadata, {
            once: true,
          });
        });
      }
    },

    getCurrentTime(): number {
      return audio.currentTime;
    },

    getDuration(): number {
      return audio.duration || 0;
    },

    isPlaying(): boolean {
      return _isPlaying;
    },

    async unload(): Promise<void> {
      audio.pause();
      audio.src = "";
      audio.load();
      _isPlaying = false;
      _duration = 0;
      _currentTime = 0;
    },

    onStateChange(callback: (state: PlaybackState) => void): () => void {
      stateListeners.add(callback);
      return () => stateListeners.delete(callback);
    },

    onTimeUpdate(callback: (time: number) => void): () => void {
      timeListeners.add(callback);
      return () => timeListeners.delete(callback);
    },

    onError(callback: (error: Error) => void): () => void {
      errorListeners.add(callback);
      return () => errorListeners.delete(callback);
    },
  };
}
